import {
  convertFilters,
  Filter,
  FilterModel,
  FilterType,
  SetFilter,
} from '@helios/server-side-util'
import {
  FieldFilters,
  QuerySort,
  RequestFilter,
} from '../../models/transactions-query.model'
import { CLIENT_ONLY_FIELDS } from '../../registry/computed-columns'
import { MATCH_STATUS_FIELD } from './match-status-filter'
import { SearchConstraint, withSearchCondition } from './search-filter'
import { QueryFieldMeta } from '../query/field-types'

/**
 * Translate ag-Grid's filter and sort models into the Flex Transactions /query contract.
 *
 * The whole payload is derived here, in pure functions, precisely so the request BODY is what tests
 * assert against: every way this can go wrong is invisible at every layer above it. A dynamic key
 * sent to the wrong bucket comes back as unfiltered rows with no error at all
 * (`FAIL_ON_UNKNOWN_PROPERTIES` is disabled globally), and a sort on a name the backend does not know
 * sorts on nulls — which looks exactly like "the user has not sorted".
 *
 * Four rules are worth reading before changing anything.
 *
 *  1. **`isDynamic` decides the bucket, and nothing else does.** Not the group, not the source, not a
 *     static-name list (PVAL-26129 §3). The backend publishes the flag per column on both per-source
 *     `fields` endpoints; the group rule this replaces got a real case wrong — an Enfusion `tran_data`
 *     column can arrive in the DEFAULT group and still be dynamic.
 *
 *  2. **A field this builder does not recognise never reaches the request.** `meta` (built from the
 *     source's catalog) is the allow-list. The grid carries columns no backend knows — the computed
 *     Helios columns, the match-count columns the UI keys by target (`matchCount:cust`) — and naming
 *     one is a silent no-op rather than an error. {@link CLIENT_ONLY_FIELDS} additionally drops a
 *     UI-computed name that a per-client catalog happens to also publish.
 *
 *  3. **The converter is reused, never reimplemented.** `convertFilters` from
 *     `@helios/server-side-util` already emits this contract's filter bodies, including the uppercase
 *     `AND` / `OR` join operator. This module only ROUTES its output. Writing a second converter here
 *     is how the two would drift.
 *
 *  4. **Client-only filter surfaces are not part of this.** Status chips, quick filters, the
 *     cross-filter, the FX-currency filter and quick-search stay client-side over the loaded page
 *     (§7) and never reach a payload — they are applied through ag-Grid's external filter, not its
 *     filter model, so they cannot arrive here in the first place.
 */

/**
 * Fields that are catalogued on both sources but have NO backend filter slot, so a filter on one is
 * silently ignored (§3a).
 *
 * `externalReferenceSource` is the whole list today. It is dropped from the payload here AND has its
 * header filter turned off in the ColDef factory — both, because either alone leaves a hole: without
 * the ColDef change a user can still build a predicate that does nothing, and without this a
 * predicate restored from a saved filter group would still be sent. Raised with the backend as a
 * follow-up; delete the entry once it has a slot.
 */
export const UNFILTERABLE_FIELDS: ReadonlySet<string> = new Set([
  'externalReferenceSource',
])

/**
 * Display placeholders that must never reach the wire as a set value.
 *
 * The grid renders an absent value as an em dash, and for a set filter the rendered token is also the
 * filter value unless the column says otherwise. Sent as a set value it matches nothing against a
 * text slot, and fails outright against a typed one — `accountId`'s `SetFilter<Int>` slot cannot
 * deserialize `'—'`, and a dynamic key cast to a number or boolean is a Postgres error (a 500).
 *
 * Normalised to `null` — a real "is null" predicate — rather than dropped: dropping it would silently
 * turn "show me the rows with no value" into "show me everything".
 *
 * The col-def factory is the primary defence (every set filter it builds emits raw values or the
 * boolean tokens); this is the backstop for a set value produced anywhere else, such as a predicate
 * restored from a saved filter group.
 */
const DISPLAY_PLACEHOLDERS: ReadonlySet<string> = new Set(['—', 'N/A'])

/** Replace any display placeholder in a set filter's values with `null`; anything else unchanged. */
const scrubPlaceholders = (filter: Filter): Filter => {
  if (filter.filterType !== FilterType.Set) return filter
  const set = filter as SetFilter
  const values = set.values
  const isPlaceholder = (value: unknown): boolean =>
    typeof value === 'string' && DISPLAY_PLACEHOLDERS.has(value)
  if (!values?.some(isPlaceholder)) return filter
  // Typed as SetFilter rather than returned inline: with `Filter` as the contextual type, the object
  // literal's `values` would trip TS's excess-property check.
  const scrubbed: SetFilter = {
    ...set,
    values: values.map(value => (isPlaceholder(value) ? null : value)),
  }
  return scrubbed
}

/** ag-Grid's sort model entry, as `gridApi.getSortModel()` returns it, in precedence order. */
export interface GridSortModelEntry {
  colId: string
  sort: string
}

export interface RequestFilterInput {
  /** `gridApi.getFilterModel()`, verbatim. */
  filterModel: FilterModel<Record<string, unknown>>
  /** `gridApi.getSortModel()`, verbatim — already in the user's chosen precedence order. */
  sortModel: readonly GridSortModelEntry[]
  /**
   * Per-field query metadata for the source's catalog, from `buildFieldMeta`. Doubles as the
   * allow-list (rule 2) and as the source of each field's bucket and sort type.
   */
  meta: ReadonlyMap<string, QueryFieldMeta>
  /**
   * The status checkboxes' server-side constraint, from `serverMatchStatusValues`: the `matchStatus`
   * values to allow, or `null`/absent for no constraint. See `match-status-filter.ts`.
   */
  matchStatus?: readonly string[] | null
  /**
   * The toolbar search box: a text `contains` on the source's search column (see `search-filter.ts`),
   * or `null`/absent when the box is empty. Routed by the column's `isDynamic` like any other filter,
   * and dropped if the catalog does not carry the column.
   */
  search?: SearchConstraint | null
}

/**
 * Whether a field may be sent at all: the catalog has to know it, it has to have a filter slot, and
 * it must not be a column whose value this UI computes.
 */
const isSendable = (
  field: string,
  meta: ReadonlyMap<string, QueryFieldMeta>,
): boolean =>
  meta.has(field) &&
  !UNFILTERABLE_FIELDS.has(field) &&
  !CLIENT_ONLY_FIELDS.has(field)

/**
 * Route the applied filter model into the two-bucket contract, or `null` when nothing survives.
 *
 * `null` rather than `{}` is deliberate and load-bearing twice over: it is what the contract means by
 * "no filter", and it is what makes the refetch snapshot of an all-dropped model byte-identical to the
 * unfiltered one — so filtering only on client-only columns issues no request at all.
 *
 * `dynamicFilters` is omitted entirely rather than sent empty, per §3.
 */
export function buildRequestFilter(
  input: RequestFilterInput,
): RequestFilter | null {
  const { filterModel, meta } = input

  // The converter's own `filterType` is kept rather than restamped from `meta`: it describes the
  // SHAPE that was actually built (a `set` entry has `values`, a scalar one has `filters[]`), the
  // grid's filter for the column was itself derived from the same resolved type, and the shape is
  // what the backend has to read. `meta.filterType` is what picked the widget in the first place.
  const converted = convertFilters(filterModel) as Record<string, Filter>

  const statics: FieldFilters = {}
  const dynamics: FieldFilters = {}

  Object.entries(converted).forEach(([field, raw]) => {
    if (!isSendable(field, meta)) return
    const entry = scrubPlaceholders(raw)
    // Rule 1. `isDynamic === true` is the only thing consulted.
    if (meta.get(field)?.isDynamic === true) {
      dynamics[field] = entry
      return
    }
    statics[field] = entry
  })

  const search = input.search
  if (search && search.value && isSendable(search.field, meta)) {
    // Rule 1 again: the search column's bucket is its own `isDynamic`, nothing else.
    const bucket =
      meta.get(search.field)?.isDynamic === true ? dynamics : statics
    bucket[search.field] = withSearchCondition(
      bucket[search.field] as Filter | undefined,
      search.value,
    )
  }

  if (input.matchStatus) {
    statics[MATCH_STATUS_FIELD] = withStatusConstraint(
      statics[MATCH_STATUS_FIELD],
      input.matchStatus,
    )
  }

  const hasStatics = Object.keys(statics).length > 0
  const hasDynamics = Object.keys(dynamics).length > 0
  if (!hasStatics && !hasDynamics) return null

  const filter: RequestFilter = { ...statics }
  if (hasDynamics) filter.dynamicFilters = dynamics
  return filter
}

/**
 * Fold the status checkboxes' constraint into whatever `matchStatus` filter the column itself carries.
 *
 * Always the STATIC bucket, and not gated on the catalog: `matchStatus` is a promoted column with a
 * `SetFilter<String>` slot on both read models, so it is part of the contract whether or not a given
 * catalog lists it — and under `dynamicFilters` it would be the static-name collision 400.
 *
 * A Match Status header filter and the checkboxes are two predicates on one field, and the backend
 * takes one filter per field — so the payload is their INTERSECTION, which is also what the grid shows
 * (the header filter and the checkboxes' client-side pass both have to accept a row). A header filter
 * that is not a set (it cannot be one today: the slot is a set) is replaced by the checkboxes'.
 */
const withStatusConstraint = (
  existing: unknown,
  allowed: readonly string[],
): SetFilter => {
  const current = existing as Partial<SetFilter> | undefined
  const values =
    current?.filterType === FilterType.Set && Array.isArray(current.values)
      ? current.values.filter(
          (value): value is string =>
            typeof value === 'string' && allowed.includes(value),
        )
      : [...allowed]
  return { filterType: FilterType.Set, values }
}

/**
 * Build the `sort` array in the user's precedence order, or `null` for an empty sort model.
 *
 * `type` is ALWAYS present (§5). It is only consulted for dynamic keys — a static column sorts by its
 * SQL type — but omitting it makes the backend fall back to a text comparison, so a numeric dynamic
 * column would sort 1000 before 9 with nothing on screen to say why.
 *
 * `null` (not `[]`) for an empty model, so the backend applies its own default order:
 * `transactionId, transactionType` for Enfusion and `extTranId` for Prometheus.
 *
 * The same allow-list as the filter applies, for a sharper reason: a sort on a name the backend does
 * not know is NOT an error. Any non-static name is treated as a dynamic key, so it sorts on nulls —
 * which silently does not sort at all.
 */
export function buildSort(input: RequestFilterInput): QuerySort[] | null {
  const { sortModel, meta } = input

  const sort: QuerySort[] = []
  sortModel.forEach(entry => {
    const field = entry.colId
    // UNFILTERABLE_FIELDS is deliberately NOT consulted here: a missing FILTER slot says nothing
    // about sortability, and `externalReferenceSource` is a real promoted column that sorts fine.
    if (!meta.has(field) || CLIENT_ONLY_FIELDS.has(field)) return
    const fieldMeta = meta.get(field)
    // A static field the backend declares unsortable is a 400 for the WHOLE request, not a no-op.
    // Its header has no sort control, so this only catches a sort restored from saved column state.
    if (!fieldMeta?.sortable) return
    sort.push({
      field,
      direction: entry.sort === 'desc' ? 'DESC' : 'ASC',
      type: fieldMeta.sortType,
    })
  })

  return sort.length ? sort : null
}

/**
 * The serialized filter + sort snapshot the refetch guard compares against (§7).
 *
 * `applyRows()` calls `gridApi.onFilterChanged()`, and so do the status chips, the quick filters and
 * the cross-filter — so "the filter changed" is not a reliable signal that the SERVER-side predicate
 * changed. A refetch fires only when this string differs from the one the current page was fetched
 * with, which is what keeps a chip toggle at zero requests and a column-filter change at exactly one.
 *
 * Derived from the BUILT payload rather than from the raw grid models on purpose: a filter on a
 * client-only column changes `getFilterModel()` but not the payload, and it must not cost a request
 * that would return the same rows.
 */
export function requestSnapshot(input: RequestFilterInput): string {
  return serializeRequest(buildRequestFilter(input), buildSort(input))
}

/**
 * The one serialization of an already-built filter + sort, shared by {@link requestSnapshot} and by
 * the component when it stamps the payload it is about to send. Two copies of this would be two
 * definitions of "the same request", and the zero/one-request guard depends on them agreeing.
 */
export function serializeRequest(
  filter: RequestFilter | null,
  sort: QuerySort[] | null,
): string {
  return JSON.stringify({ filter, sort })
}
